// « Ma mise en page » (issue #235) : un modèle HTML tiré de la première page
// du CV déposé, rempli ensuite avec le profil par des balises Mustache.
//
// L'IA écrit le modèle UNE fois, au dépôt du CV (Radar, formule Complet) ; le
// rendu, lui, ne coûte rien et suit chaque CV adapté. Essai du 2026-10-20 sur
// trois vrais CV (gpt-5.4-mini) : mise en page très fidèle en 8 s pour environ
// un cent, mais une réponse sur quatre inventait une balise (`{{^@last}}`) qui
// s'affichait en clair dans le CV. D'où ce fichier : la consigne, une
// validation qui REFUSE plutôt que de corriger, et le remplissage.
//
// LA VALIDATION EST UNE LISTE BLANCHE, pas une liste noire. Le modèle est du
// HTML écrit par une IA qui a lu un document fourni par l'utilisateur : il
// s'affiche dans l'aperçu et s'imprime depuis une page de Radar. Seuls des
// éléments de mise en page, des attributs inertes et les balises du profil
// passent ; un script, un gestionnaire `on*`, une ressource externe (`url(`,
// `@import`, `<link>`) ou une balise inconnue font refuser le modèle entier.
// Le service redemande alors une fois, puis renonce : un modèle refusé n'est
// jamais proposé (`apps/console/src/services/miseEnPage.ts`).

/** La consigne envoyée avec l'image de la première page. */
export const CONSIGNE_MISE_EN_PAGE = `Tu reçois l'image de la PREMIÈRE page d'un CV. Écris un MODÈLE HTML qui reproduit sa mise en page le plus fidèlement possible (colonnes, bandeaux, couleurs, polices proches parmi les polices système, tailles, filets, position des dates, position des coordonnées), mais SANS son contenu : le contenu viendra d'un profil JSON par des balises Mustache.

Règles strictes :
- Un seul document HTML complet, CSS dans une balise <style>. Aucun <script>, aucune ressource externe (ni police web, ni image, ni url()), aucun attribut on*.
- Format A4 portrait : @page { size: A4; margin: 0 } et une mise en page qui s'étire sur plusieurs pages si le contenu est long (pas de hauteur fixe, pas de position absolute pour le contenu).
- Si l'original a une photo, prévois son emplacement avec {{#photo}}<img src="{{photo}}" alt="">{{/photo}}.
- Un lien de profil s'écrit <a href="{{url}}">{{url}}</a>, l'adresse <a href="mailto:{{basics.email}}">{{basics.email}}</a>, jamais autrement.
- Balises disponibles, et SEULEMENT celles-ci :
  {{basics.name}} {{basics.label}} {{basics.email}} {{basics.phone}} {{basics.location}} {{basics.summary}}
  {{#basics.profiles}}{{network}} {{url}}{{/basics.profiles}}
  {{#work}}{{position}} {{name}} {{startDate}} {{endDate}} {{summary}} {{#highlights}}{{.}}{{/highlights}}{{/work}}
  {{#education}}{{studyType}} {{area}} {{institution}} {{startDate}} {{endDate}}{{/education}}
  {{#skills}}{{.}}{{/skills}}
  {{#languages}}{{language}} {{fluency}}{{/languages}}
  {{#projects}}{{name}} {{description}} {{#highlights}}{{.}}{{/highlights}}{{/projects}}
  Titres de section : {{titres.present}} {{titres.profil}} {{titres.experience}} {{titres.formation}} {{titres.competences}} {{titres.langues}} {{titres.projets}} {{titres.contact}}
- Une section vide ne doit rien afficher : entoure chaque section de {{#work.length}}…{{/work.length}} (idem education, skills, languages, projects) et le résumé de {{#basics.summary}}…{{/basics.summary}}.
- Pour tester qu'une liste n'est pas vide, utilise TOUJOURS {{#liste.length}}…{{/liste.length}}, jamais {{#liste}} deux fois imbriqué. {{#liste}} sert seulement à répéter.
- Aucune autre forme de balise : ni {{{…}}}, ni {{&…}}, ni {{!…}}, ni {{>…}}, ni @first, @last, @index.
- Chaque champ du profil apparaît UNE seule fois dans le document (pas de compétences à la fois dans la colonne et dans le corps).
- Tailles en pt, comme un vrai CV A4 : corps de texte entre 9 et 10,5 pt, titres de section 11 à 13 pt, nom 20 à 28 pt. Marges intérieures de 12 à 18 mm. Les compétences d'une colonne étroite s'affichent en ligne, séparées par des virgules ou des puces, pas une par ligne.
- Une date de fin vide veut dire « aujourd'hui » : écris {{endDate}}{{^endDate}}{{titres.present}}{{/endDate}}.
- Réponds par le HTML seul, sans bloc de code.`

/** Le HTML de la réponse, sans le bloc de code que le modèle ajoute parfois. */
export function extraireModele(reponse: string): string {
  return reponse.trim().replace(/^```(?:html)?\s*/i, '').replace(/\s*```$/, '').trim()
}

// --- La liste blanche --------------------------------------------------------

const ELEMENTS = new Set([
  'html', 'head', 'body', 'meta', 'title', 'style',
  'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br', 'hr',
  'section', 'header', 'footer', 'aside', 'main', 'article', 'nav', 'address', 'figure', 'figcaption',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'colgroup', 'col', 'caption',
  'strong', 'b', 'em', 'i', 'u', 'small', 'sup', 'sub', 'time', 'blockquote',
  'a', 'img',
])

const ATTRIBUTS = new Set([
  'class', 'id', 'style', 'lang', 'dir', 'title', 'role', 'alt', 'colspan', 'rowspan', 'width', 'height',
  'charset', 'name', 'content', 'href', 'src', 'scope', 'span',
])

const MAX_TAILLE = 60_000

/**
 * Les raisons de refuser un modèle ; une liste vide, il passe.
 *
 * Refuser, jamais nettoyer : un modèle dont on aurait retiré un morceau ne
 * ressemble plus à ce que l'IA voulait dessiner, et un nettoyage incomplet
 * laisserait passer ce qu'il n'a pas prévu.
 */
export function validerMiseEnPage(modele: string): string[] {
  const raisons: string[] = []
  if (!modele || modele.length > MAX_TAILLE) return ['taille']
  const sansCommentaires = modele.replace(/<!--[\s\S]*?-->/g, '').replace(/<!doctype[^>]*>/i, '')
  if (/url\s*\(|@import|expression\s*\(|javascript:|behavior\s*:|-moz-binding/i.test(sansCommentaires)) raisons.push('ressource externe')

  // Les éléments et leurs attributs.
  const balise = /<\/?([a-zA-Z][\w-]*)((?:\s+[^\s=>/]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*\/?>/g
  const reste = sansCommentaires.replace(balise, (_t, nom: string, attrs: string) => {
    const el = nom.toLowerCase()
    if (!ELEMENTS.has(el)) raisons.push(`élément ${el}`)
    for (const m of attrs.matchAll(/([^\s=>/]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s>]+))?/g)) {
      const attr = m[1].toLowerCase()
      const valeur = (m[2] ?? '').replace(/^["']|["']$/g, '').trim()
      if (!ATTRIBUTS.has(attr) && !attr.startsWith('aria-')) raisons.push(`attribut ${attr}`)
      else if (attr === 'href' && valeur !== '{{url}}' && valeur !== 'mailto:{{basics.email}}') raisons.push('lien')
      else if (attr === 'src' && valeur !== '{{photo}}') raisons.push('image')
      else if (el === 'meta' && attr === 'content' && !/^width=device-width/.test(valeur)) raisons.push('meta')
    }
    return ''
  })
  // Un `<` qui n'ouvre aucune balise reconnue : une balise mal formée, que
  // le navigateur lirait peut-être autrement que cette expression.
  if (/<[a-zA-Z/!?]/.test(reste)) raisons.push('balise mal formée')

  // Les balises Mustache : seulement les formes que `remplirModele` sait
  // lire, et des sections fermées. Une autre forme (`{{^@last}}`, `{{&x}}`)
  // s'afficherait en clair dans le CV : c'était l'échec de l'essai. Un nom
  // inconnu, lui, ne fait que rendre vide (ou faux, pour une section).
  const pile: string[] = []
  for (const m of modele.matchAll(/\{\{([^}]*)\}\}/g)) {
    const brut = m[1]
    const t = /^\s*([#^/]?)\s*([\w.]+)\s*$/.exec(brut)
    if (!t) {
      raisons.push(`balise {{${brut}}}`)
      continue
    }
    if (t[1] === '#' || t[1] === '^') pile.push(t[2])
    else if (t[1] === '/' && pile.pop() !== t[2]) raisons.push(`section ${t[2]} mal fermée`)
  }
  if (pile.length) raisons.push(`section ${pile[pile.length - 1]} non fermée`)
  if (/\{\{\{|\}\}\}/.test(modele)) raisons.push('triple accolade')
  return [...new Set(raisons)]
}

// --- Le remplissage ----------------------------------------------------------

const echapper = (s: unknown): string =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

function lire(pile: unknown[], cle: string): unknown {
  if (cle === '.') return pile[pile.length - 1]
  for (let i = pile.length - 1; i >= 0; i--) {
    let v: any = pile[i]
    let trouve = true
    for (const p of cle.split('.')) {
      if (v != null && typeof v === 'object' && p in v) v = v[p]
      else { trouve = false; break }
    }
    if (trouve) return v
  }
  return undefined
}

/**
 * Le sous-ensemble de Mustache que la consigne autorise : valeurs échappées,
 * sections répétées sur une liste, conditionnelles sur une valeur vraie,
 * sections inversées. Lève sur une section non fermée : le modèle a passé la
 * validation, l'appelant traite l'exception comme un modèle refusé.
 */
export function remplirModele(modele: string, contexte: object): string {
  const rendre = (tpl: string, pile: unknown[]): string => {
    let sortie = ''
    let i = 0
    const re = /\{\{\s*([#^/]?)\s*([\w.]+)\s*\}\}/g
    for (;;) {
      re.lastIndex = i
      const m = re.exec(tpl)
      if (!m) return sortie + tpl.slice(i)
      sortie += tpl.slice(i, m.index)
      const [tout, signe, cle] = m
      if (signe === '#' || signe === '^') {
        // La fermeture qui correspond, en comptant les sections du même nom.
        const re2 = new RegExp(`\\{\\{\\s*([#^/])\\s*${cle.replace(/\./g, '\\.')}\\s*\\}\\}`, 'g')
        re2.lastIndex = m.index + tout.length
        let profondeur = 1
        let fin = -1
        let longueur = 0
        let m2: RegExpExecArray | null
        while ((m2 = re2.exec(tpl))) {
          if (m2[1] !== '/') profondeur++
          else if (--profondeur === 0) { fin = m2.index; longueur = m2[0].length; break }
        }
        if (fin < 0) throw new Error(`section ${cle} non fermée`)
        const corps = tpl.slice(m.index + tout.length, fin)
        const v = lire(pile, cle)
        const vrai = Array.isArray(v) ? v.length > 0 : Boolean(v)
        if (signe === '#') {
          if (Array.isArray(v)) sortie += v.map((e) => rendre(corps, [...pile, e])).join('')
          else if (vrai) sortie += rendre(corps, [...pile, v])
        } else if (!vrai) sortie += rendre(corps, pile)
        i = fin + longueur
      } else if (signe === '/') {
        i = m.index + tout.length
      } else {
        const v = lire(pile, cle)
        sortie += typeof v === 'object' ? '' : echapper(v)
        i = m.index + tout.length
      }
    }
  }
  return rendre(modele, [contexte])
}

/**
 * Pose ce que le modèle de l'IA ne garantit pas : le titre qui nomme le PDF
 * (`documentName.ts`), la langue du document, et un centrage à l'écran pour
 * l'aperçu. L'impression garde la mise en page du modèle.
 */
export function encadrerMiseEnPage(html: string, titre: string, langue: string): string {
  const tete = `<meta charset="utf-8"><title>${echapper(titre)}</title><style>@media screen { html { background: #e5e7eb; } body { margin: 0 auto !important; max-width: 210mm; background: #fff; } }</style>`
  let doc = /<html[\s>]/i.test(html) ? html : `<!doctype html><html><head></head><body>${html}</body></html>`
  doc = doc.replace(/<title>[\s\S]*?<\/title>/i, '').replace(/<meta\s+charset[^>]*>/i, '')
  doc = /<head[^>]*>/i.test(doc) ? doc.replace(/<head[^>]*>/i, (h) => `${h}${tete}`) : doc.replace(/<html[^>]*>/i, (h) => `${h}<head>${tete}</head>`)
  return doc.replace(/<html(?![^>]*\blang=)([^>]*)>/i, `<html lang="${echapper(langue)}"$1>`)
}
